'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import {
  useWorkspaceActions,
  useWorkspaceItem,
} from '@/components/workspace/store';

/**
 * Shared resize gesture hook used by widget frames (`NodeFrame`) and
 * annotation nodes. Both surfaces want the same UX: invisible hit zones
 * along the edges + corners of the node that flip the cursor to `*-resize`
 * on hover and let the user drag to grow / shrink in flow space, with no
 * visible bounding-box chrome.
 *
 * Why a shared hook and not a shared component:
 *   - Each consumer wants the hit zones in a specific stacking position
 *     relative to its own chrome (e.g. NodeFrame mounts them above the
 *     resize edge but below the corner badge; AnnotationNode mounts
 *     them above its shape SVG). Exposing the handlers keeps the JSX
 *     wiring in the consumer.
 *   - The math is identical — read start state on pointer-down, convert
 *     screen deltas to flow units via the current zoom, snap to a 24px
 *     lattice, clamp to min/max, and commit with `persist: false` during
 *     the gesture / debounced PATCH on end.
 */
export type ResizeAnchor =
  | 't'
  | 'l'
  | 'r'
  | 'b'
  | 'tl'
  | 'tr'
  | 'bl'
  | 'br';

// Module-scoped set of itemIds with an active custom resize gesture.
// React Flow's internal ResizeObserver emits `dimensions` change events
// every time a node's measured size changes — including the changes our
// own `useNodeResize` just committed. The canvas's `onNodesChange`
// echoes those back into the store as `{ width, height }` patches,
// which OVERWRITES the snapped width while leaving our committed `x`
// untouched. On left/top resizes that mismatch shows up as the right
// (or bottom) edge jittering by a pixel or two each frame.
//
// We can't fully suppress RF's internal measurement, but we can stop
// echoing it back into our store while a custom gesture owns the size.
// Callers register their itemId here on `beginResize` and clear it on
// `endResize`; `workspace-canvas.tsx` consults `isAnyResizing` /
// `isResizingItem` from inside `onNodesChange`.
const activeResizes = new Set<string>();

export function isAnyResizing(): boolean {
  return activeResizes.size > 0;
}

export function isResizingItem(itemId: string): boolean {
  return activeResizes.has(itemId);
}

/**
 * Hold a node's size against RF's measurement echo while something other
 * than a resize gesture owns it, and return the release. Added 2026-09-21 for
 * the app window's native fullscreen (content-node's maximize): the browser's
 * `:fullscreen` UA sheet forces the `.react-flow__node` wrapper to the
 * viewport (`width: 100% !important`), RF's ResizeObserver measures that and
 * emits a `dimensions` change with no `resizing` flag, and the canvas would
 * write the viewport size into the item (`persist: false`, but the node is
 * rebuilt from it) so the window came back viewport-sized on exit. Same
 * registry the resize gesture uses, so the canvas needs no second check.
 * Holding an item has no other effect: `isAnyResizing` has no consumer.
 *
 * NO CALLER SINCE 2026-09-29 (lane D; the Stage algorithm, rule 12). Full
 * screen no longer makes the node the browser's full-screen element: the
 * PAGE goes full screen and the canvas draws the node at a display-only
 * size, which the dimension echo already skips (the handset clamp's
 * `handsetClampedRef` set in workspace-canvas.tsx). Kept, small and tested,
 * for the next thing that has to own a node's size outside a gesture.
 */
export function holdNodeSize(itemId: string): () => void {
  activeResizes.add(itemId);
  return () => {
    activeResizes.delete(itemId);
  };
}

export interface UseNodeResizeOptions {
  itemId: string;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  /**
   * Optional 24px-or-similar grid step used by the snap pass. Pass `0`
   * to disable snapping entirely (annotations want finer control than
   * widgets when sizing small stickies, for example).
   */
  snapStep?: number;
}

export interface UseNodeResizeResult {
  beginResize: (
    e: React.PointerEvent<HTMLElement>,
    anchor: ResizeAnchor,
  ) => void;
  onResizeMove: (e: React.PointerEvent<HTMLElement>) => void;
  endResize: (e: React.PointerEvent<HTMLElement>) => void;
  /** True while a resize gesture is in flight. */
  isResizingRef: React.MutableRefObject<boolean>;
}

export function useNodeResize({
  itemId,
  minWidth = 80,
  minHeight = 48,
  maxWidth = 5000,
  maxHeight = 5000,
  snapStep = 24,
}: UseNodeResizeOptions): UseNodeResizeResult {
  const { patchItemState, bringToFront } = useWorkspaceActions();
  // Subscribe ONLY to our own item — subscribing to the full items
  // array would have us re-rendering every consumer of the hook on
  // every patch (each frame of a resize, drag, or remote mutation).
  // Stashing the live item in a ref lets begin / end callbacks read
  // the latest snapshot without entering the React dep array,
  // breaking the storm.
  const item = useWorkspaceItem(itemId);
  const itemRef = useRef(item);
  itemRef.current = item;
  const flow = useReactFlow();

  const resizeRef = useRef<{
    pointerId: number;
    anchor: ResizeAnchor;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);
  const isResizingRef = useRef(false);
  // rAF coalescing — pointermove can fire 200+ Hz on a trackpad. We only
  // need one store patch per animation frame, so we stash the latest
  // pointer position in this ref and let the rAF callback drain it.
  // Without this, the store + ReactFlow re-render every event, which
  // produces visible edge flicker and stutter on workspaces with more
  // than a handful of connected nodes.
  const pendingMoveRef = useRef<{ clientX: number; clientY: number } | null>(
    null,
  );
  const rafRef = useRef<number | null>(null);

  const beginResize = useCallback(
    (e: React.PointerEvent<HTMLElement>, anchor: ResizeAnchor) => {
      // Ignore non-primary buttons. A right-click on the edge area
      // should fall through to the workspace context menu, not start
      // a resize that we'd then have to abort on the contextmenu
      // event. Same goes for middle-click and back/forward buttons.
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const it = itemRef.current;
      if (!it) return;
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* element was detached between pointerdown and the capture call */
      }
      bringToFront(itemId);
      resizeRef.current = {
        pointerId: e.pointerId,
        anchor,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: it.state.x,
        startY: it.state.y,
        startW: it.state.width,
        startH: it.state.height,
      };
      isResizingRef.current = true;
      activeResizes.add(itemId);
    },
    [itemId, bringToFront],
  );

  // Cleanup any pending rAF on unmount so we don't fire a store update
  // against a detached component. Also drain the module-scoped set so
  // we don't leave a stale entry if the user navigates away mid-drag.
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      activeResizes.delete(itemId);
    };
  }, [itemId]);

  // Esc cancels an in-flight resize: restore the gesture's start rect
  // and drop the gesture state (any queued rAF frame included, so a
  // trailing pointermove can't re-land the abandoned size). Capture
  // phase + preventDefault so the cancel wins over other Esc consumers
  // (panel close, tool disarm). Ref-check no-op unless THIS node owns
  // an active gesture; later pointer frames no-op via `resizeRef`.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const start = resizeRef.current;
      if (!start || e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingMoveRef.current = null;
      resizeRef.current = null;
      isResizingRef.current = false;
      activeResizes.delete(itemId);
      patchItemState(
        itemId,
        { x: start.startX, y: start.startY, width: start.startW, height: start.startH },
        { debounceMs: 200 },
      );
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [itemId, patchItemState]);

  const flushMove = useCallback(() => {
    rafRef.current = null;
    const pending = pendingMoveRef.current;
    pendingMoveRef.current = null;
    const start = resizeRef.current;
    if (!pending || !start) return;
    const z = flow.getZoom() || 1;
    const dx = (pending.clientX - start.startClientX) / z;
    const dy = (pending.clientY - start.startClientY) / z;

    let nextX = start.startX;
    let nextY = start.startY;
    let nextW = start.startW;
    let nextH = start.startH;

    const hasL = start.anchor.includes('l');
    const hasR = start.anchor.includes('r');
    const hasT = start.anchor.includes('t');
    const hasB = start.anchor.includes('b');

    if (hasR) nextW = start.startW + dx;
    if (hasL) {
      nextW = start.startW - dx;
      nextX = start.startX + dx;
    }
    if (hasB) nextH = start.startH + dy;
    if (hasT) {
      nextH = start.startH - dy;
      nextY = start.startY + dy;
    }

    // Clamp first: the dragged axes only. An axis the anchor does not move
    // keeps its start value exactly, even outside the bounds (fix round 2,
    // 2026-09-30: a resize changes, and snaps, only the axes being dragged;
    // the snap below was already gated, the clamp was not).
    let clampedW = hasL || hasR ? Math.max(minWidth, Math.min(maxWidth, nextW)) : nextW;
    let clampedH = hasT || hasB ? Math.max(minHeight, Math.min(maxHeight, nextH)) : nextH;

    // If clamp adjusted the size and we're anchored on the moving
    // edge, slide x/y back so the OPPOSITE edge stays put.
    if (hasL) {
      const wDelta = clampedW - nextW;
      nextX -= wDelta;
    }
    if (hasT) {
      const hDelta = clampedH - nextH;
      nextY -= hDelta;
    }

    // Snap moving edge to the 24px lattice. CRITICAL: only snap the
    // dimension(s) the anchor actually moves. Previously we snapped
    // BOTH width and height for every anchor — so dragging the top
    // edge would round the (unchanged) startW down to the nearest
    // 24px multiple and the right edge would silently shift left,
    // which is exactly the "the other corner moves" bug. By gating
    // the snap on hasL/hasR (W) and hasT/hasB (H) we leave the
    // un-touched dimension exactly at startW/startH.
    if (snapStep > 0) {
      const wMoving = hasL || hasR;
      const hMoving = hasT || hasB;
      if (wMoving) {
        const snappedW = Math.round(clampedW / snapStep) * snapStep;
        if (hasL) nextX = start.startX + start.startW - snappedW;
        clampedW = Math.max(minWidth, Math.min(maxWidth, snappedW));
      }
      if (hMoving) {
        const snappedH = Math.round(clampedH / snapStep) * snapStep;
        if (hasT) nextY = start.startY + start.startH - snappedH;
        clampedH = Math.max(minHeight, Math.min(maxHeight, snappedH));
      }
    }

    patchItemState(
      itemId,
      { x: nextX, y: nextY, width: clampedW, height: clampedH },
      { persist: false },
    );
  }, [
    flow,
    patchItemState,
    itemId,
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    snapStep,
  ]);

  // Public pointer-move handler. Records the latest pointer position
  // and queues a single rAF flush. Subsequent events that arrive in
  // the same frame are merged into the same flush — never queued
  // again — which is what caps the patch rate at the display refresh.
  const onResizeMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!resizeRef.current) return;
      pendingMoveRef.current = { clientX: e.clientX, clientY: e.clientY };
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(flushMove);
      }
    },
    [flushMove],
  );

  const endResize = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!resizeRef.current) return;
      // Drain any pending rAF synchronously so the final pointer
      // position is captured in the store before we release the
      // gesture state — otherwise a single trailing frame can land
      // a stale (start-of-frame) size on top of our debounced commit.
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (pendingMoveRef.current) flushMove();
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore — capture release after detach is a no-op */
      }
      const it = itemRef.current;
      resizeRef.current = null;
      isResizingRef.current = false;
      activeResizes.delete(itemId);
      if (it) {
        // Persist the final position + size with a short debounce so
        // the server picks up the gesture's end state. Pass values
        // explicitly to avoid closing over stale store state.
        patchItemState(
          itemId,
          {
            x: it.state.x,
            y: it.state.y,
            width: it.state.width,
            height: it.state.height,
          },
          { debounceMs: 200 },
        );
      }
    },
    [itemId, patchItemState],
  );

  return { beginResize, onResizeMove, endResize, isResizingRef };
}
